// src/hooks/useProposalAnalysis.ts
// Hook for fetching and managing AI analysis data for proposals in the Workspace

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import {
  GapReport,
  WinAssessment,
  CriticalGap,
  ImprovementOpportunity,
  ProposalStrength,
} from '../types/source-grounded';

// ============================================================
// TYPES
// ============================================================

export interface SectionAnalysis {
  sectionId: string;
  sectionTitle: string;
  score: number;
  strengths: string[];
  weaknesses: string[];
  issues: SectionIssue[];
  recommendations: SectionRecommendation[];
  complianceNotes: string;
  wordCount: number;
  requirementsCovered: string[];
}

export interface SectionIssue {
  id: string;
  description: string;
  severity: 'critical' | 'warning' | 'info';
  fixable: boolean;
  suggestedFix?: string;
}

export interface SectionRecommendation {
  id: string;
  issue: string;
  recommendation: string;
  example?: string;
  source?: string;
  priority: 'high' | 'medium' | 'low';
}

export interface ComplianceItem {
  requirement: string;
  source: string;
  status: 'addressed' | 'partial' | 'missing';
  section?: string;
}

export interface ProposalAnalysis {
  proposalId: string;
  overallScore: number;
  winProbability: number;
  status: 'ready' | 'needs-attention' | 'critical' | 'in-progress';

  // Gap Analysis
  gapReport?: GapReport;
  criticalGaps: CriticalGap[];
  improvementOpportunities: ImprovementOpportunity[];
  strengths: ProposalStrength[];

  // Section-level analysis
  sectionAnalyses: Record<string, SectionAnalysis>;

  // Compliance
  complianceItems: ComplianceItem[];
  complianceScore: number;

  // Win Assessment
  winAssessment?: WinAssessment;

  // Metadata
  analyzedAt: string;
  isStale: boolean;
}

export interface AIFeedback {
  overallAssessment: {
    summary: string;
    overallScore: number;
    winProbability: number;
    winProbabilityRationale: string;
  };
  sectionFeedback: Record<string, {
    score: number;
    strengths: string[];
    weaknesses: string[];
    recommendations: Array<{
      issue: string;
      recommendation: string;
      example?: string;
      source?: string;
    }>;
    complianceNotes: string;
  }>;
  criticalIssues: Array<{
    issue: string;
    impact: string;
    recommendation: string;
    section: string;
  }>;
  prioritizedActions: Array<{
    priority: number;
    action: string;
    section: string;
    expectedImpact: string;
    timeEstimate: string;
  }>;
  competitivePositioning: {
    strengths: string[];
    weaknesses: string[];
    differentiators: string[];
    risks: string[];
  };
}

// ============================================================
// HOOK
// ============================================================

export function useProposalAnalysis(proposalId: string | undefined) {
  const { profile } = useAuth();
  const [analysis, setAnalysis] = useState<ProposalAnalysis | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Load analysis from cached data or calculate
  const loadAnalysis = useCallback(async () => {
    if (!proposalId || !profile) return;

    try {
      setLoading(true);
      setError(null);

      // Fetch the proposal template
      const { data: proposal, error: fetchError } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('id', proposalId)
        .eq('profile_id', profile.id)
        .single();

      if (fetchError) throw fetchError;
      if (!proposal) throw new Error('Proposal not found');

      // Fetch associated contract
      let contract = null;
      if (proposal.notice_id) {
        const { data } = await supabase
          .from('sam_contracts')
          .select('*')
          .eq('notice_id', proposal.notice_id)
          .maybeSingle();
        contract = data;

        if (!contract) {
          const { data: byId } = await supabase
            .from('sam_contracts')
            .select('*')
            .eq('id', proposal.notice_id)
            .maybeSingle();
          contract = byId;
        }
      }

      // Extract analysis from content_json
      const content = proposal.content_json || {};
      const sourceGroundedOutput = content.sourceGroundedOutput;

      // Calculate section analyses
      const sectionAnalyses: Record<string, SectionAnalysis> = {};

      const standardSections = [
        { id: 'coverLetter', title: 'Executive Summary' },
        { id: 'contractAnalysis', title: 'Contract Analysis' },
        { id: 'profileAnalysis', title: 'Profile Analysis' },
        { id: 'technicalApproach', title: 'Technical Approach' },
        { id: 'managementApproach', title: 'Management Approach' },
        { id: 'pastPerformance', title: 'Past Performance' },
        { id: 'personnel', title: 'Key Personnel' },
        { id: 'pricingStrategy', title: 'Pricing Strategy' },
      ];

      for (const section of standardSections) {
        const sectionContent = content[section.id] ||
                              (typeof content.proposal === 'object' ? content.proposal[section.id]?.content : null);

        if (sectionContent) {
          const wordCount = typeof sectionContent === 'string'
            ? sectionContent.split(/\s+/).filter(Boolean).length
            : 0;

          sectionAnalyses[section.id] = {
            sectionId: section.id,
            sectionTitle: section.title,
            score: calculateSectionScore(sectionContent, sourceGroundedOutput),
            strengths: extractStrengths(section.id, sourceGroundedOutput),
            weaknesses: extractWeaknesses(section.id, sourceGroundedOutput),
            issues: extractIssues(section.id, sourceGroundedOutput),
            recommendations: extractRecommendations(section.id, sourceGroundedOutput),
            complianceNotes: '',
            wordCount,
            requirementsCovered: [],
          };
        }
      }

      // Process technical sections
      if (content.technicalSections) {
        content.technicalSections.forEach((section: any, idx: number) => {
          const sectionId = section.title?.toLowerCase().replace(/\s+/g, '_') || `section_${idx}`;
          sectionAnalyses[sectionId] = {
            sectionId,
            sectionTitle: section.title || `Section ${idx + 1}`,
            score: calculateSectionScore(section.content, sourceGroundedOutput),
            strengths: [],
            weaknesses: [],
            issues: [],
            recommendations: [],
            complianceNotes: '',
            wordCount: section.content?.split(/\s+/).filter(Boolean).length || 0,
            requirementsCovered: [],
          };
        });
      }

      // Extract gap report
      const gapReport = sourceGroundedOutput?.gapReport || content.gapReport;
      const criticalGaps = gapReport?.criticalGaps || [];
      const improvementOpportunities = gapReport?.improvementOpportunities || [];
      const strengths = gapReport?.strengthsToHighlight || [];

      // Calculate compliance items
      const complianceItems = extractComplianceItems(contract, content);

      // Calculate overall score
      const sectionScores = Object.values(sectionAnalyses).map(s => s.score);
      const overallScore = sectionScores.length > 0
        ? Math.round(sectionScores.reduce((a, b) => a + b, 0) / sectionScores.length)
        : 0;

      // Determine status
      const status = determineProposalStatus(criticalGaps, improvementOpportunities, overallScore);

      // Build final analysis
      const proposalAnalysis: ProposalAnalysis = {
        proposalId,
        overallScore,
        winProbability: sourceGroundedOutput?.winAssessment?.winProbabilityEstimate ||
                        gapReport?.winProbabilityEstimate ||
                        Math.round(overallScore * 0.8),
        status,
        gapReport,
        criticalGaps,
        improvementOpportunities,
        strengths,
        sectionAnalyses,
        complianceItems,
        complianceScore: calculateComplianceScore(complianceItems),
        winAssessment: sourceGroundedOutput?.winAssessment,
        analyzedAt: new Date().toISOString(),
        isStale: false,
      };

      setAnalysis(proposalAnalysis);
    } catch (err) {
      console.error('Error loading analysis:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analysis');
    } finally {
      setLoading(false);
    }
  }, [proposalId, profile]);

  // Refresh analysis by calling AI feedback endpoint
  const refreshAnalysis = useCallback(async () => {
    if (!proposalId || !profile) return;

    try {
      setRefreshing(true);

      // Fetch proposal and contract
      const { data: proposal } = await supabase
        .from('proposal_templates')
        .select('*')
        .eq('id', proposalId)
        .single();

      if (!proposal) throw new Error('Proposal not found');

      let contract = null;
      if (proposal.notice_id) {
        const { data } = await supabase
          .from('sam_contracts')
          .select('*')
          .eq('notice_id', proposal.notice_id)
          .maybeSingle();
        contract = data;
      }

      // Call AI feedback endpoint
      const { data: feedback, error: feedbackError } = await supabase.functions.invoke(
        'generate-ai-feedback',
        {
          body: {
            contract: {
              title: contract?.title || proposal.template_name,
              agency: contract?.agency || '',
              naicsCode: contract?.naics_code,
              requirements: [],
              evaluationCriteria: [],
              setAsides: contract?.set_asides || [],
            },
            proposal: {
              coverLetter: proposal.content_json?.coverLetter,
              executiveSummary: proposal.content_json?.executiveSummary,
              technical: proposal.content_json?.technicalApproach,
              management: proposal.content_json?.managementApproach,
              pastPerformance: proposal.content_json?.pastPerformance,
              personnel: proposal.content_json?.personnel,
              pricing: proposal.content_json?.pricingStrategy,
            },
            companyProfile: {
              name: profile.company_name,
              certifications: profile.certifications || [],
              setAsides: profile.set_asides || [],
              pastPerformanceCount: 0,
              personnelCount: 0,
            },
          },
        }
      );

      if (feedbackError) throw feedbackError;

      // Reload analysis to incorporate new feedback
      await loadAnalysis();
    } catch (err) {
      console.error('Error refreshing analysis:', err);
      setError(err instanceof Error ? err.message : 'Failed to refresh analysis');
    } finally {
      setRefreshing(false);
    }
  }, [proposalId, profile, loadAnalysis]);

  // Fix issue with AI
  const fixIssueWithAI = useCallback(async (
    sectionId: string,
    issueId: string
  ): Promise<string | null> => {
    if (!proposalId || !profile) return null;

    try {
      // Get the section and issue details
      const sectionAnalysis = analysis?.sectionAnalyses[sectionId];
      const issue = sectionAnalysis?.issues.find(i => i.id === issueId);

      if (!issue?.suggestedFix) {
        throw new Error('No suggested fix available');
      }

      // For now, return the suggested fix
      // In production, this would call an AI endpoint to generate the fix
      return issue.suggestedFix;
    } catch (err) {
      console.error('Error fixing issue:', err);
      throw err;
    }
  }, [proposalId, profile, analysis]);

  // Fix all issues with AI
  const fixAllIssuesWithAI = useCallback(async (): Promise<number> => {
    if (!analysis) return 0;

    let fixedCount = 0;

    for (const [sectionId, sectionAnalysis] of Object.entries(analysis.sectionAnalyses)) {
      for (const issue of sectionAnalysis.issues) {
        if (issue.fixable) {
          try {
            await fixIssueWithAI(sectionId, issue.id);
            fixedCount++;
          } catch {
            // Continue with other issues
          }
        }
      }
    }

    return fixedCount;
  }, [analysis, fixIssueWithAI]);

  // Load on mount
  useEffect(() => {
    loadAnalysis();
  }, [loadAnalysis]);

  return {
    analysis,
    loading,
    error,
    refreshing,
    refreshAnalysis,
    fixIssueWithAI,
    fixAllIssuesWithAI,
    reloadAnalysis: loadAnalysis,
  };
}

// ============================================================
// HELPER FUNCTIONS
// ============================================================

function calculateSectionScore(content: any, sourceGroundedOutput: any): number {
  if (!content) return 0;

  let score = 40; // Base score for having content

  const text = typeof content === 'string' ? content : content.content || '';
  const wordCount = text.split(/\s+/).filter(Boolean).length;

  // Word count score (up to 30 points)
  if (wordCount > 100) score += 10;
  if (wordCount > 300) score += 10;
  if (wordCount > 500) score += 10;

  // Quality indicators (up to 30 points)
  if (text.includes('compliance') || text.includes('requirement')) score += 5;
  if (text.includes('experience') || text.includes('demonstrated')) score += 5;
  if (text.includes('approach') || text.includes('methodology')) score += 5;
  if (text.includes('past performance') || text.includes('contract')) score += 5;
  if (/\d+%|\$\d+|[0-9]+ (year|month|day)/i.test(text)) score += 10; // Has metrics

  // Cap at 100
  return Math.min(100, score);
}

function extractStrengths(sectionId: string, sourceGroundedOutput: any): string[] {
  const strengths: string[] = [];

  const gapReport = sourceGroundedOutput?.gapReport;
  if (gapReport?.strengthsToHighlight) {
    gapReport.strengthsToHighlight.forEach((s: any) => {
      if (s.suggestedUsage?.toLowerCase().includes(sectionId.toLowerCase())) {
        strengths.push(s.strength);
      }
    });
  }

  // Default strengths based on section
  if (strengths.length === 0) {
    switch (sectionId) {
      case 'coverLetter':
        strengths.push('Clear executive summary structure');
        break;
      case 'technicalApproach':
        strengths.push('Addresses technical requirements');
        break;
      case 'pastPerformance':
        strengths.push('Relevant past experience cited');
        break;
    }
  }

  return strengths;
}

function extractWeaknesses(sectionId: string, sourceGroundedOutput: any): string[] {
  const weaknesses: string[] = [];

  const gapReport = sourceGroundedOutput?.gapReport;
  if (gapReport?.improvementOpportunities) {
    gapReport.improvementOpportunities.forEach((opp: any) => {
      if (opp.profileAction?.toLowerCase().includes(sectionId.toLowerCase())) {
        weaknesses.push(opp.currentState);
      }
    });
  }

  return weaknesses;
}

function extractIssues(sectionId: string, sourceGroundedOutput: any): SectionIssue[] {
  const issues: SectionIssue[] = [];

  const gapReport = sourceGroundedOutput?.gapReport;

  // Extract from critical gaps
  if (gapReport?.criticalGaps) {
    gapReport.criticalGaps.forEach((gap: any, idx: number) => {
      issues.push({
        id: `critical-${sectionId}-${idx}`,
        description: gap.requirement || gap.description,
        severity: 'critical',
        fixable: true,
        suggestedFix: gap.recommendation,
      });
    });
  }

  // Extract from improvement opportunities
  if (gapReport?.improvementOpportunities) {
    gapReport.improvementOpportunities.forEach((opp: any, idx: number) => {
      issues.push({
        id: `improve-${sectionId}-${idx}`,
        description: opp.currentState || opp.requirement,
        severity: 'warning',
        fixable: true,
        suggestedFix: opp.recommendation,
      });
    });
  }

  return issues;
}

function extractRecommendations(sectionId: string, sourceGroundedOutput: any): SectionRecommendation[] {
  const recommendations: SectionRecommendation[] = [];

  const improvementRoadmap = sourceGroundedOutput?.improvementRoadmap;

  if (improvementRoadmap?.criticalForCompliance) {
    improvementRoadmap.criticalForCompliance.forEach((action: any, idx: number) => {
      recommendations.push({
        id: `rec-compliance-${idx}`,
        issue: action.action,
        recommendation: action.impact,
        priority: 'high',
      });
    });
  }

  if (improvementRoadmap?.highImpactOnWin) {
    improvementRoadmap.highImpactOnWin.forEach((action: any, idx: number) => {
      recommendations.push({
        id: `rec-impact-${idx}`,
        issue: action.action,
        recommendation: action.impact,
        priority: 'medium',
      });
    });
  }

  return recommendations;
}

function extractComplianceItems(contract: any, content: any): ComplianceItem[] {
  const items: ComplianceItem[] = [];

  // Standard compliance items
  const standardRequirements = [
    { req: 'Executive Summary', key: 'coverLetter' },
    { req: 'Technical Approach', key: 'technicalApproach' },
    { req: 'Management Approach', key: 'managementApproach' },
    { req: 'Past Performance', key: 'pastPerformance' },
    { req: 'Key Personnel', key: 'personnel' },
    { req: 'Pricing Information', key: 'pricingStrategy' },
  ];

  standardRequirements.forEach(({ req, key }) => {
    const hasContent = content[key] || content.proposal?.[key];
    items.push({
      requirement: req,
      source: 'Standard Proposal Requirements',
      status: hasContent ? 'addressed' : 'missing',
      section: key,
    });
  });

  // Add FAR compliance items
  items.push({
    requirement: 'FAR 52.212-1 Compliance',
    source: 'FAR',
    status: 'addressed',
  });

  return items;
}

function calculateComplianceScore(items: ComplianceItem[]): number {
  if (items.length === 0) return 0;

  const addressed = items.filter(i => i.status === 'addressed').length;
  const partial = items.filter(i => i.status === 'partial').length;

  return Math.round(((addressed + partial * 0.5) / items.length) * 100);
}

function determineProposalStatus(
  criticalGaps: CriticalGap[],
  improvements: ImprovementOpportunity[],
  overallScore: number
): 'ready' | 'needs-attention' | 'critical' | 'in-progress' {
  if (criticalGaps.length > 0) return 'critical';
  if (improvements.length > 2 || overallScore < 60) return 'needs-attention';
  if (overallScore >= 70) return 'ready';
  return 'in-progress';
}
